import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const DIA_MS = 24 * 60 * 60 * 1000;

export const STATUS_ASSINATURA = {
    ACTIVE: { label: 'Ativa', tom: 'good' },
    OVERDUE: { label: 'Em atraso', tom: 'warning' },
    INACTIVE: { label: 'Inativa', tom: 'neutral' },
    EXPIRED: { label: 'Expirada', tom: 'neutral' },
    CANCELED: { label: 'Cancelada', tom: 'critical' },
    CANCELLED: { label: 'Cancelada', tom: 'critical' },
};

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Busca todos os endpoints em paralelo. Um endpoint com erro não derruba a dashboard:
// cada bloco recebe seu próprio estado de erro.
async function buscarDados(token) {
    const headers = { Authorization: token };
    const get = (rota) => axios.get(`${API}${rota}`, { headers }).then(r => r.data);

    const [assinaturas, usuarios, planos, leads, cancelamentos, descontos] = await Promise.allSettled([
        get('/listarAssinaturas').then(d => d.assinaturas || []),
        get('/listarUsuarios').then(d => d.usuarios || []),
        get('/listarPlanos').then(d => d.planos || []),
        get('/leads/recuperaveis').then(d => (Array.isArray(d) ? d : [])),
        get('/listarCancelamentos').then(d => d.cancelamentos || []),
        get('/listarDescontos').then(d => d.descontos || []),
    ]);

    // Uso de cada cupom (endpoint é por desconto)
    let usosDescontos = { status: 'rejected' };
    if (descontos.status === 'fulfilled') {
        usosDescontos = await Promise.allSettled(
            descontos.value.map(d =>
                get(`/getDescontosUsados/${d.idDesconto}`).then(r => ({
                    idDesconto: d.idDesconto,
                    usos: (r.descontosUsados || []).length,
                }))
            )
        ).then(res => ({
            status: 'fulfilled',
            value: res.filter(r => r.status === 'fulfilled').map(r => r.value),
        }));
    }

    const unwrap = (r) => (r.status === 'fulfilled' ? { data: r.value, erro: null } : { data: [], erro: r.reason });
    return {
        assinaturas: unwrap(assinaturas),
        usuarios: unwrap(usuarios),
        planos: unwrap(planos),
        leads: unwrap(leads),
        cancelamentos: unwrap(cancelamentos),
        descontos: unwrap(descontos),
        usosDescontos: unwrap(usosDescontos),
    };
}

function contar(lista, chave) {
    const counts = {};
    lista.forEach(item => {
        const k = chave(item);
        counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
}

function calcularMetricas(raw) {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const assinaturas = raw.assinaturas.data;
    const usuarios = raw.usuarios.data;
    const planos = raw.planos.data;

    const ativas = assinaturas.filter(a => a.status === 'ACTIVE');

    // Valor mensal do plano: usa o que vier na assinatura, senão cruza com /listarPlanos
    const planoPorId = new Map(planos.map(p => [p.idPlano, p]));
    const planoPorNome = new Map(planos.map(p => [p.nome, p]));
    const planoDa = (a) =>
        planoPorId.get(a.plano?.idPlano ?? a.idPlano) || planoPorNome.get(a.plano?.nome) || a.plano || {};
    const valorMensal = (a) => Number(planoDa(a).valorNovoMensal) || 0;

    const receitaMensal = ativas.reduce((soma, a) => soma + valorMensal(a), 0);

    // Status
    const porStatus = Object.entries(contar(assinaturas, a => a.status || 'DESCONHECIDO'))
        .map(([status, total]) => ({
            status,
            total,
            label: STATUS_ASSINATURA[status]?.label || status,
            tom: STATUS_ASSINATURA[status]?.tom || 'neutral',
        }))
        .sort((a, b) => b.total - a.total);

    // Assinaturas ativas e receita por plano (ordem dos planos cadastrados)
    const ativasPorPlano = contar(ativas, a => planoDa(a).nome || 'Sem plano');
    const nomesPlanos = [...new Set([...planos.map(p => p.nome), ...Object.keys(ativasPorPlano)])];
    const porPlano = nomesPlanos.map(nome => ({
        nome,
        ativas: ativasPorPlano[nome] || 0,
        receita: ativas.filter(a => (planoDa(a).nome || 'Sem plano') === nome)
            .reduce((s, a) => s + valorMensal(a), 0),
    }));

    // Novas assinaturas nos últimos 12 meses (pela data de início)
    const meses = [];
    for (let i = 11; i >= 0; i--) {
        const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
        meses.push({ ano: d.getFullYear(), mes: d.getMonth(), label: `${MESES[d.getMonth()]}/${String(d.getFullYear()).slice(2)}`, total: 0 });
    }
    assinaturas.forEach(a => {
        if (!a.dataInicio) return;
        const d = new Date(a.dataInicio);
        const m = meses.find(x => x.ano === d.getUTCFullYear() && x.mes === d.getUTCMonth());
        if (m) m.total += 1;
    });
    const mesAtual = meses[meses.length - 1].total;
    const mesAnterior = meses[meses.length - 2].total;

    // Cobranças: atrasadas (data já passou) e próximas 7 dias
    const comCobranca = ativas
        .filter(a => a.proximaCobranca)
        .map(a => {
            const d = new Date(a.proximaCobranca);
            const dia = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
            return { ...a, diasAte: Math.round((dia - hoje) / DIA_MS), valor: valorMensal(a), nomePlano: planoDa(a).nome };
        });
    const cobrancasAtrasadas = comCobranca.filter(a => a.diasAte < 0).sort((a, b) => a.diasAte - b.diasAte);
    const proximasCobrancas = comCobranca.filter(a => a.diasAte >= 0 && a.diasAte <= 7).sort((a, b) => a.diasAte - b.diasAte);

    // Usuários
    const usuariosPJ = usuarios.filter(u => u.Cnpj).length;
    const idsAssinantes = new Set(ativas.map(a => a.usuario?.idUsuario ?? a.idUsuario).filter(Boolean));
    const assinantes = idsAssinantes.size || ativas.length;
    const conversao = usuarios.length ? Math.min(assinantes / usuarios.length, 1) : null;

    // Leads recuperáveis por etapa do cadastro
    const leadsPorStep = Object.entries(contar(raw.leads.data, l => l.stepAtual ?? '?'))
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([step, total]) => ({ step, total }));

    // Motivos de cancelamento (um cancelamento pode ter vários motivos)
    const motivos = Object.entries(contar(
        raw.cancelamentos.data.flatMap(c => c.motivosSelecionados || []),
        m => m
    ))
        .map(([motivo, total]) => ({ motivo, total }))
        .sort((a, b) => b.total - a.total);

    // Cupons
    const usosPorId = new Map(raw.usosDescontos.data.map(u => [u.idDesconto, u.usos]));
    const cupons = raw.descontos.data
        .map(d => ({ ...d, usos: usosPorId.get(d.idDesconto) ?? 0 }))
        .sort((a, b) => b.usos - a.usos);

    return {
        totalAssinaturas: assinaturas.length,
        ativas: ativas.length,
        receitaMensal,
        ticketMedio: ativas.length ? receitaMensal / ativas.length : 0,
        porStatus,
        porPlano,
        novasPorMes: meses,
        novasMesAtual: mesAtual,
        novasMesAnterior: mesAnterior,
        cobrancasAtrasadas,
        proximasCobrancas,
        totalUsuarios: usuarios.length,
        usuariosPJ,
        conversao,
        totalLeads: raw.leads.data.length,
        leadsPorStep,
        totalCancelamentos: raw.cancelamentos.data.length,
        motivos,
        cupons,
        cuponsAtivos: cupons.filter(c => c.status).length,
        totalUsosCupons: cupons.reduce((s, c) => s + c.usos, 0),
    };
}

export function useDashboardData() {
    const [raw, setRaw] = useState(null);
    const [loading, setLoading] = useState(true);
    const [atualizadoEm, setAtualizadoEm] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true);
        const dados = await buscarDados(localStorage.getItem('token'));
        setRaw(dados);
        setAtualizadoEm(new Date());
        setLoading(false);
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const metricas = useMemo(() => (raw ? calcularMetricas(raw) : null), [raw]);
    const erros = useMemo(() => {
        if (!raw) return {};
        return Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, Boolean(v.erro)]));
    }, [raw]);

    return { metricas, erros, loading, atualizadoEm, recarregar: carregar };
}
